import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import CalPopupTrigger from "./CalPopupTrigger";

const NAV = [
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/market-notes", label: "Market Notes" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-kingdom/95 backdrop-blur-xl border-b border-white/5"
          : "bg-kingdom/80 backdrop-blur-sm border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo-link"
          className="flex items-center gap-3 group"
        >
          <img
            src="/assets/logo/huddleup-logo-gold.png"
            alt="HuddleUp"
            className="h-9 w-9 object-contain transition-transform duration-700 group-hover:rotate-[8deg]"
          />
          <span
            className="font-sans text-paper text-[13px] font-medium"
            style={{ letterSpacing: "0.32em" }}
          >
            HUDDLEUP
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] uppercase font-sans tracking-[0.18em] transition-colors duration-500 ${
                  isActive ? "text-gold" : "text-warm hover:text-paper"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
          <Link
            to="/diagnostic"
            data-testid="header-cta"
            className="btn-gold"
          >
            Find Your First Move
          </Link>
          <CalPopupTrigger>
            {({ onClick }) => (
              <button
                type="button"
                onClick={onClick}
                data-testid="header-book-call"
                className="hidden lg:inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-warm/70 hover:text-gold transition-colors duration-500"
              >
                Book a call
              </button>
            )}
          </CalPopupTrigger>
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-paper p-2"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-kingdom border-t border-white/5 px-6 py-8"
        >
          <nav className="flex flex-col gap-6">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-warm uppercase text-sm tracking-[0.2em]"
              >
                {n.label}
              </NavLink>
            ))}
            <Link
              to="/diagnostic"
              data-testid="mobile-header-cta"
              className="btn-gold mt-4 self-start"
            >
              Find Your First Move
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
